import * as React from 'react';
import { TransformWrapper, TransformComponent } from '@kokarn/react-zoom-pan-pinch';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';

import { Loading } from 'components';
import { IBuilding } from 'models';
import { useGetDecalQuery } from 'store/apis/decal.api';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { getBuildingLocation } from 'utils';
import { useDecalFormManager } from './decal-form.hook';

interface DecalFormProps {
  zoneId: number;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const DecalForm = React.forwardRef(
  ({ zoneId, onInit = () => {}, onSubmitting = () => {}, next }: DecalFormProps, ref) => {
    const [{ zone, formManager, loading_form, loading_submit }] = useDecalFormManager({ decalId: zoneId, next });

    const { orgSlug } = useAppSelector(selectCurrentUserInfo);
    const { data: decalSvg } = useGetDecalQuery({ orgSlug, id: zoneId });
    const [decalImageLoading, setDecalImageLoading] = React.useState(true);
    const theme = useTheme();
    const down_sm = useMediaQuery(theme.breakpoints.down('sm'));

    const decalSvgBlob = new Blob([decalSvg], { type: 'image/svg+xml' });
    const decalImageUrl = URL.createObjectURL(decalSvgBlob);

    const buildingLocation = zone?.building ? getBuildingLocation(zone.building as IBuilding) : '';
    const zoneCode = zone?.code || '';
    const zoneLocation = zone?.name || '';
    const zoneFloor = zone?.floor?.floor || '';

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loading_form), [loading_form]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return (
      <form onSubmit={formManager.handleSubmit}>
        <Box sx={{ paddingTop: 2 }}>
          <Grid container rowSpacing={3} columnSpacing={5}>
            <Grid item xs={down_sm ? 12 : 7}>
              <Box
                sx={{
                  position: 'relative',
                }}
              >
                <TransformWrapper initialScale={1} minScale={0.25} maxScale={2} wheel={{ step: 0.1 }}>
                  {() => (
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '250px',
                        background: theme.palette.grey[100],
                        borderRadius: 8,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img
                        id="decal-svg"
                        src={decalImageUrl}
                        alt="decal"
                        onLoad={() => setDecalImageLoading(false)}
                        style={{
                          width: '100%',
                          height: '250px',
                        }}
                      />
                    </TransformComponent>
                  )}
                </TransformWrapper>

                {decalImageLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: 250,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: theme.palette.grey[100],
                      borderRadius: 1.8,
                    }}
                  >
                    <Loading text="Loading Decal..." />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item xs={down_sm ? 12 : 5}>
              <Grid container rowSpacing={2} columnSpacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Building
                  </Typography>
                  <Typography variant="body2">{buildingLocation}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Number
                  </Typography>
                  <Typography variant="body2">{zoneCode}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Location
                  </Typography>
                  <Typography variant="body2">{zoneLocation}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    Floor
                  </Typography>
                  <Typography variant="body2">{zoneFloor}</Typography>
                </Grid>

                {/* <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    See something you don&apos;t like? Go to the&nbsp;
                    <Link
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        navigate(ROUTES.COMPANY_SETTINGS);
                      }}
                    >
                      Company Settings
                    </Link>{' '}
                    page to change your brand color or company logo.
                  </Typography>
                </Grid> */}
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </form>
    );
  },
);
